import { useState, useMemo, useEffect } from "react";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { MessageSquare, Plus } from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { FAQFormDialog } from "./faq/FAQForm";
import { FAQDetailView } from "./faq/FAQDetails";
import { FAQTable } from "./faq/FAQTable";
import { useFaqList, useSearchFAQ, useFaqCategory } from "../hooks";
import { useUtilityServices } from "@features/mx/readCycleManagement/hooks";
import { useSearchParams } from "react-router-dom";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  utility: string;
  status: "active" | "inactive";
  order: number;
  helpful: number;
}

// FAQ filters interface
interface FAQFilters {
  remote_utility_id: string;
  is_pagination_required: any;
  page: number;
  limit: number;
  show_inactive?: any;
  faq_category?: string;
}

export const FAQSettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const remoteUtilityId = getRemoteUtilityId().toString();

  // Read search query and filters from URL
  const searchQuery = decodeURIComponent(searchParams.get("query") || "");
  const filterCategory = searchParams.get("category") || "";

  // Reset to first page when search query or filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterCategory]);

  // Compute filters using useMemo (without search_data)
  const filters = useMemo((): FAQFilters => {
    return {
      remote_utility_id: remoteUtilityId,
      is_pagination_required: true,
      page: page,
      limit: PAGE_SIZE,
      show_inactive: true,
      ...(filterCategory && { faq_category: filterCategory }),
    };
  }, [remoteUtilityId, page, filterCategory]);

  // Search parameters for search API (only when there's a search query)
  const searchApiParams = useMemo(() => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      return {
        remote_utility_id: remoteUtilityId,
        search_data: "",
        search_field: "faq",
      };
    }
    return {
      remote_utility_id: remoteUtilityId,
      search_data: searchQuery,
      search_field: "faq",
    };
  }, [remoteUtilityId, searchQuery]);

  // Use search API when there's a search query, otherwise use regular API
  const { data: regularData, refetch: refetchRegular } = useFaqList(filters);
  const { data: searchData, refetch: refetchSearch } = useSearchFAQ(searchApiParams);

  // Determine which data to use
  const data = searchQuery && searchQuery.trim().length > 0 ? searchData : regularData;
  const refetch = searchQuery && searchQuery.trim().length > 0 ? refetchSearch : refetchRegular;

  // Update page when URL page param changes
  useEffect(() => {
    const pageParam = searchParams.get("page");
    if (pageParam) {
      setPage(parseInt(pageParam, 10));
    }
  }, [searchParams]);

  // Get utilities and categories from API
  const { data: utilitiesData } = useUtilityServices();
  const { data: categoriesData } = useFaqCategory({
    remote_utility_id: remoteUtilityId
  });

  const { toast } = useToast();

  // Transform API data to match FAQ interface
  const faqs = useMemo(() => {
    // Handle search API response structure
    if (searchQuery && searchQuery.trim().length > 0) {
      const results = data?.results || data?.result || data || [];

      return results.map((item: any, index: number) => ({
        id: item.code || item.id || `search-${index}`,
        question: item.name || item.question || "",
        answer: item.answer || "",
        category: item.faqCategory || "",
        utility: item.utilityService || "",
        status: item.isActive ? ("active" as const) : ("inactive" as const),
        order: index + 1,
        helpful: item.isHelpful || 0,
      }));
    }

    // Handle regular API response structure
    const results = data?.result || [];

    return results.map((item: any, index: number) => ({
      id: item.code,
      question: item.name,
      answer: item.answer,
      category: item.faq_category || item.faqCategory,
      utility: item.utility_service || item.utilityService,
      status: item.isActive || item.isActive ? ("active" as const) : ("inactive" as const),
      order: (page - 1) * PAGE_SIZE + index + 1,
      helpful: item.isHelpful || 0,
    }));
  }, [data, searchQuery, page]);

  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);
  const [viewingFAQ, setViewingFAQ] = useState<FAQ | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  // Handle search functionality
  const handleSearch = (searchTerm: string, paramName: "search_data") => {
    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      params.set("query", searchTerm);
    } else {
      params.delete("query");
    }

    // Reset to page 1 when searching
    params.set("page", "1");
    setPage(1);

    setSearchParams(params);
  };

  const handleApplyFilters = (filters: { category?: string }) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("category");
    params.delete("page");

    // Add new filter params
    if (filters.category) {
      params.set("category", filters.category);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setPage(1);

    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("category");
    params.delete("page");
    params.delete("query");

    setPage(1);
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
    setPage(newPage);
  };

  // Get utilities from API, fallback to FAQ data utilities
  const apiUtilities = utilitiesData
    ? utilitiesData
        .filter((utility) => utility.isActive === true)
        .map((utility) => utility.name)
    : [];

  const faqUtilities = [
    ...new Set(faqs.map((faq) => faq.utility).filter(Boolean)),
  ] as string[];
  const utilities = apiUtilities.length > 0 ? apiUtilities : faqUtilities;

  // Get category options from API
  const categoryOptions = categoriesData?.result?.map(cat => cat.value) || [];

  const handleSaveFAQ = (faqData: Partial<FAQ>) => {
    if (editingFAQ) {
      toast({
        title: "FAQ Updated",
        description: "The FAQ has been successfully updated.",
      });
    } else {
      toast({
        title: "FAQ Created",
        description: "A new FAQ has been successfully created.",
      });
    }
    setEditingFAQ(null);
    setIsDialogOpen(false);
  };

  const handleDeleteFAQ = (id: string) => {
    // TODO: Call API to delete FAQ
    toast({
      title: "FAQ Deleted",
      description: "The FAQ has been successfully deleted.",
    });
  };

  const handleViewFAQ = (faq: FAQ) => {
    setViewingFAQ(faq);
    setIsViewDialogOpen(true);
  };

  const handleEditFAQ = (faq: FAQ) => {
    setEditingFAQ(faq);
    setIsDialogOpen(true);
  };

  const handleToggleStatus = (faq: FAQ) => {
    // TODO: Call API to toggle FAQ status
    const statusText = faq.status === "active" ? "inactive" : "active";
    toast({
      title: "Status Updated",
      description: `FAQ status changed to ${statusText}`,
    });
    refetch();
  };

  // Current filter values for the filter panel
  const currentFilters = useMemo(() => ({
    category: filterCategory,
  }), [filterCategory]);

  // Pagination object
  const pagination = useMemo(() => {
    if (searchQuery && searchQuery.trim().length > 0) {
      // For search results, we might not have pagination info, so calculate based on results length
      const totalItems = data?.results?.length || data?.result?.length || 0;
      return {
        currentPage: 1, // Search results are typically on single page
        totalPages: 1,
        totalItems,
        startItem: 1,
        endItem: totalItems,
        itemsPerPage: totalItems,
      };
    } else {
      // Regular pagination for non-search results
      return data
        ? {
            currentPage: page,
            totalPages: Math.ceil((data?.count || 0) / PAGE_SIZE),
            totalItems: data?.count || 0,
            itemsPerPage: PAGE_SIZE,
            startItem: (page - 1) * PAGE_SIZE + 1,
            endItem: Math.min(page * PAGE_SIZE, data?.count || 0),
          }
        : undefined;
    }
  }, [data, searchQuery, page]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="page-subheading flex items-center gap-2"><MessageSquare />FAQ Management</CardTitle>
              <CardDescription className="page-subtitle">
                Create and manage frequently asked questions to help users find
                quick answers.
              </CardDescription>
            </div>
            <Button
              onClick={() => {
                setEditingFAQ(null);
                setIsDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add FAQ
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <FAQTable
            faqs={faqs}
            onView={handleViewFAQ}
            onEdit={handleEditFAQ}
            onDelete={handleDeleteFAQ}
            onToggleStatus={handleToggleStatus}
            onRefetch={refetch}
            searchPlaceholder="Search FAQs by question, category, or utility..."
            searchParamName="query"
            searchDebounceMs={300}
            onSearch={handleSearch}
            currentFilters={currentFilters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            categoryOptions={categoryOptions}
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      {isDialogOpen && (
        <FAQFormDialog
          faq={editingFAQ}
          utilities={utilities}
          onSave={handleSaveFAQ}
          onRefetch={refetch}
          onCancel={() => {
            setEditingFAQ(null);
            setIsDialogOpen(false);
          }}
        />
      )}

      <FAQDetailView
        faq={viewingFAQ}
        isOpen={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
      />
    </div>
  );
};